import type { User } from "@/services/api/user";
import { getDisplayName as getDisplayNameBase, getInitials } from "@/lib/user";
import { formatDate } from "@/lib/dateFormat";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import RowActionsMenu from "@/components/ActionCells/RowActionsMenu";
import type { RowAction } from "@/components/ActionCells/RowActionsMenu";
import PencilIcon from "@/components/SVGComponents/PencilIcon";
import CrossIcon from "@/components/SVGComponents/CrossIcon";
import toast from "@/lib/toast";
import styles from "../TeamRoles.module.scss";

interface AdminCardProps {
  admin: User;
  onRemove: (admin: User) => void;
}

const getDisplayName = (user: User) => getDisplayNameBase(user, { fallbackLabel: "Admin" });
const formatLastActive = (value: string | null) => formatDate(value, { day: "numeric" });

const getRowActions = (admin: User, onRemove: (admin: User) => void): RowAction[] => {
  const name = getDisplayName(admin);
  return [
    {
      key: "edit",
      label: "Edit access",
      icon: <PencilIcon />,
      onClick: () =>
        toast.warning(
          `Admin management isn't connected to a real API yet — ${name}'s access wasn't changed.`,
        ),
    },
    {
      key: "remove",
      label: "Remove admin",
      icon: <CrossIcon withBg={false} stroke="currentColor" strokeWidth={1.5} width={14} height={14} />,
      tone: "danger",
      dividerBefore: true,
      onClick: () => onRemove(admin),
    },
  ];
};

const AdminCard = ({ admin, onRemove }: AdminCardProps) => {
  const name = getDisplayName(admin);
  const initials = getInitials(name);
  const mobile = admin?.mobileNo || "—";
  const lastActive = formatLastActive(admin?.lastActiveAt) || "—";
  const email = admin?.email || "—";

  return (
    <div className={styles.mobileCard}>
      <div className={styles.mobileCardHeader}>
        <div className={styles.mobileAvatar}>
          <span>{initials}</span>
        </div>

        <div className={styles.mobileAdminInfo}>
          <h3 className={styles.mobileAdminName}>{name}</h3>
          <p className={styles.mobileAdminEmail}>{email}</p>
        </div>

        <div className={styles.mobileCardActions}>
          <StatusBadge
            label={admin?.isActive ? "ACTIVE" : "INACTIVE"}
            tone={admin?.isActive ? "success" : "neutral"}
            className={styles.mobileStatusBadge}
          />
          <RowActionsMenu
            actions={getRowActions(admin, onRemove)}
            ariaLabel={`Actions for ${name}`}
            triggerClassName={styles.mobileRowActionTrigger}
          />
        </div>
      </div>

      <div className={styles.mobileCardDivider} />

      <div className={styles.mobileCardGrid}>
        <div className={styles.mobileCardField}>
          <span className={styles.mobileFieldLabel}>Mobile</span>
          <span className={styles.mobileFieldValue}>{mobile}</span>
        </div>

        <div className={styles.mobileCardField}>
          <span className={styles.mobileFieldLabel}>Last Active</span>
          <span className={styles.mobileFieldValue}>{lastActive}</span>
        </div>
      </div>
    </div>
  );
};

export default AdminCard;
